import React, { useState, useMemo } from 'react';
import { Card, LoadingSpinner } from 'components/common';
import PropTypes from 'prop-types';
import ReactECharts from 'echarts-for-react';
import { FiLayers } from 'react-icons/fi';
import { CHART_FONT, CHART_TOOLTIP } from 'helpers/echartsTheme';
import ContractDrillDownPanel from './ContractDrillDownPanel';

const fmtM = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
};
const fmtW = (n) => `${Number(n || 0).toFixed(1)} MT`;

const PALETTE = {
  contracts: { bg: '#3B82F6', light: '#EFF6FF', text: '#1D4ED8', label: 'Contracts' },
  addendums: { bg: '#F59E0B', light: '#FFFBEB', text: '#B45309', label: 'Addendums' }
};

const METRICS = [
  { key: 'count',  label: 'Count',    fmt: v => Number(v || 0).toLocaleString() },
  { key: 'value',  label: 'Value $',  fmt: fmtM },
  { key: 'weight', label: 'Weight',   fmt: fmtW },
];

const escapeHtml = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

const ContractCategoryChart = ({ data, loading, error, baseParams }) => {
  const [metric,     setMetric]     = useState('count');
  const [drillOpen,  setDrillOpen]  = useState(false);
  const [drillTitle, setDrillTitle] = useState('');
  const [drillExtra, setDrillExtra] = useState({});
  const [drillMode,  setDrillMode]  = useState('contracts');

  const items        = useMemo(() => data || [], [data]);
  const activeMetric = METRICS.find(m => m.key === metric) || METRICS[0];
  const total        = items.reduce((s, r) => s + Number(r[metric] || 0), 0);

  const donutOption = useMemo(() => ({
    textStyle: { fontFamily: CHART_FONT.fontFamily },
    tooltip: total === 0 ? { show: false } : {
      ...CHART_TOOLTIP,
      formatter: (p) =>
        `<b>${escapeHtml(p.name)}</b><br/>${activeMetric.label}: <b>${escapeHtml(activeMetric.fmt(p.value))}</b> (${p.percent}%)`
    },
    graphic: [
      {
        type: 'text', left: 'center', top: 66, silent: true,
        style: { text: activeMetric.fmt(total), fontSize: 14, fontWeight: 800, fill: '#111827', fontFamily: CHART_FONT.fontFamily }
      },
      {
        type: 'text', left: 'center', top: 88, silent: true,
        style: { text: 'total', fontSize: 9, fill: '#6B7280', fontFamily: CHART_FONT.fontFamily }
      }
    ],
    series: [
      total === 0
        ? {
            type: 'pie', radius: [0, 72], silent: true, label: { show: false },
            data: [{ value: 1, name: '', itemStyle: { color: '#F3F4F6' } }]
          }
        : {
            type: 'pie',
            radius: [72 * 0.55, 72],
            label: { show: false },
            itemStyle: { borderColor: '#ffffff', borderWidth: 2, opacity: 0.85 },
            emphasis: { itemStyle: { opacity: 1 }, scale: false },
            data: items.map(r => ({
              value: Number(r[metric] || 0),
              name: (PALETTE[r.category_key] || { label: r.category }).label,
              itemStyle: { color: (PALETTE[r.category_key] || { bg: '#6B7280' }).bg }
            }))
          }
    ]
  }), [items, metric, total, activeMetric]);

  if (loading) return (
    <Card className="tw-p-6 tw-border tw-border-gray-200">
      <div className="tw-flex tw-justify-center tw-py-10"><LoadingSpinner size="lg" /></div>
    </Card>
  );
  if (error) return (
    <Card className="tw-p-4 tw-bg-red-50 tw-border tw-border-red-200">
      <p className="tw-text-red-600 tw-text-sm">Error loading category data</p>
    </Card>
  );

  const handleClick = (row) => {
    setDrillTitle(row.category);
    setDrillExtra({});
    setDrillMode(row.category_key === 'addendums' ? 'addendums' : 'contracts');
    setDrillOpen(true);
  };

  return (
    <>
      <Card className="tw-border tw-border-gray-200 tw-bg-white tw-shadow-sm">
        <div className="tw-px-5 tw-py-4 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-justify-between">
          <div className="tw-flex tw-items-center tw-gap-2">
            <FiLayers size={16} className="tw-text-purple-500" />
            <h3 className="tw-font-bold tw-text-gray-900">Contract Category</h3>
          </div>
          <div className="tw-inline-flex tw-rounded tw-border tw-border-gray-200 tw-overflow-hidden">
            {METRICS.map((opt, idx) => (
              <button
                key={opt.key}
                onClick={() => setMetric(opt.key)}
                className={`tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-transition-all tw-cursor-pointer tw-border-0 ${idx > 0 ? 'tw-border-l tw-border-l-gray-200' : ''} ${metric === opt.key ? 'tw-bg-blue-600 tw-text-white' : 'tw-bg-white tw-text-gray-500 hover:tw-bg-gray-50 hover:tw-text-gray-800'}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="tw-p-5 tw-flex tw-items-start tw-gap-5 tw-flex-wrap">
          {/* Donut */}
          <div className="tw-flex-shrink-0 tw-relative" style={{ width: 170, height: 170 }}>
            <ReactECharts
              option={donutOption}
              style={{ width: 170, height: 170 }}
              notMerge
              onEvents={{
                click: (params) => {
                  const row = items[params.dataIndex];
                  if (row) handleClick(row);
                }
              }}
            />
          </div>

          {/* Breakdown cards */}
          <div className="tw-flex-1 tw-space-y-3 tw-min-w-0">
            {items.map(row => {
              const palette = PALETTE[row.category_key] || { bg: '#6B7280', light: '#F3F4F6', text: '#374151', label: row.category };
              const pct     = total > 0 ? Math.round((Number(row[metric] || 0) / total) * 100) : 0;
              return (
                <div
                  key={row.category_key}
                  onClick={() => handleClick(row)}
                  className="tw-rounded-lg tw-p-3 tw-border tw-cursor-pointer hover:tw-shadow-md tw-transition-shadow"
                  style={{ borderColor: palette.bg + '40', background: palette.light }}
                >
                  <div className="tw-flex tw-items-center tw-justify-between tw-mb-1.5">
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <span className="tw-w-3 tw-h-3 tw-rounded-xl tw-flex-shrink-0" style={{ background: palette.bg }} />
                      <span className="tw-text-xs tw-font-bold" style={{ color: palette.text }}>{palette.label}</span>
                    </div>
                    <span className="tw-text-xs tw-font-bold tw-text-gray-500">{pct}%</span>
                  </div>

                  <div className="tw-flex tw-items-end tw-gap-4">
                    <div>
                      <div className="tw-text-xl tw-font-extrabold" style={{ color: palette.bg }}>
                        {Number(row.count || 0).toLocaleString()}
                      </div>
                      <div className="tw-text-xs tw-text-gray-500">{palette.label.toLowerCase()}</div>
                    </div>
                    <div>
                      <div className="tw-text-sm tw-font-bold tw-text-gray-700">{fmtM(row.value)}</div>
                      <div className="tw-text-xs tw-text-gray-400">value</div>
                    </div>
                    <div>
                      <div className="tw-text-sm tw-font-bold tw-text-gray-700">{fmtW(row.weight)}</div>
                      <div className="tw-text-xs tw-text-gray-400">weight</div>
                    </div>
                  </div>

                  <div className="tw-mt-2 tw-h-1.5 tw-bg-white tw-rounded-xl tw-overflow-hidden tw-border tw-border-gray-100">
                    <div className="tw-h-full tw-rounded-xl" style={{ width: `${pct}%`, background: palette.bg, opacity: 0.7 }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Card>

      <ContractDrillDownPanel
        open={drillOpen}
        title={drillTitle}
        baseParams={baseParams || {}}
        extraParams={drillExtra}
        onClose={() => setDrillOpen(false)}
        mode={drillMode}
      />
    </>
  );
};

ContractCategoryChart.propTypes = {
  data:       PropTypes.array,
  loading:    PropTypes.bool,
  error:      PropTypes.any,
  baseParams: PropTypes.object
};
ContractCategoryChart.defaultProps = { data: [], loading: false, error: null, baseParams: {} };

export default ContractCategoryChart;
